import builder, { Builder, withChildren } from '@builder.io/react';
import dynamic from 'next/dynamic';
import getConfig from 'next/config';
import { Component } from '@builder.io/sdk';
import buyingGuideComponents from '@/components/BuilderIO/buyingGuideComponents';
import commonComponents from '../components/BuilderIO/common';
import whyBuyFromBeComponents from '../components/BuilderIO/whyBuyFromBE';
import slimlinePromotionBanners from '../components/BuilderIO/slimlinePromotionBanners';
import formComponents from '../components/BuilderIO/forms';
import customerServiceComponents from '../components/BuilderIO/customerService';
import '@builder.io/widgets/dist/lib/builder-widgets-async';

const { NEXT_PUBLIC_BUILDER_API_KEY: apiKey } = getConfig().publicRuntimeConfig;
builder.init(apiKey);

/**
  To register new builder components, add them to the object below.
  dynamic() path has to be explicit, see: https://nextjs.org/docs/14/pages/building-your-application/optimizing/lazy-loading#examples
*/
const reactComponents = {
  SlimHeroBanner: dynamic(() => import('@/components/BuilderIO/common/SlimHeroBanner')),
  StandardHeroBanner: dynamic(() => import('@/components/BuilderIO/common/StandardHeroBanner/StandardHeroBanner')),
  LargeHeroBanner: dynamic(() => import('@/components/BuilderIO/common/LargeHeroBanner/LargeHeroBanner')),
  Breadcrumbs: dynamic(() => import('@/components/BuilderIO/common/Breadcrumbs/Breadcrumbs')),
  BuyingGuideBanner: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/BuyingGuideBanner')),
  SlimlineBanner: dynamic(() => import('@/components/BuilderIO/common/SlimlineBanner/SlimlineBanner')),
  StoneCarousel: dynamic(() => import('@/components/BuilderIO/common/StoneCarousel/StoneCarousel')),
  FaqParagraph: dynamic(() => import('@/components/BuilderIO/common/FaqParagraph/FaqParagraph')),
  DiamondCutsProgram: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/DiamondCutsProgram')),
  GatewayCopyBlock: dynamic(() => import('@/components/BuilderIO/common/GatewayCopyBlock/GatewayCopyBlock')),
  DiamondClarityDiagram: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/DiamondClarityDiagram')),
  HomepageCarousel: dynamic(() => import('@/components/BuilderIO/common/HomepageCarousel/HomepageCarousel')),
  HomepageCarouselUpdated: dynamic(
    () => import('@/components/BuilderIO/common/HomepageCarouselUpdated/HomepageCarouselUpdated')
  ),
  PromoHeroBanner: dynamic(() => import('@/components/BuilderIO/common/PromoHeroBanner/PromoHeroBanner')),
  LinkTile: dynamic(() => import('@/components/BuilderIO/common/LinkTile/LinkTile')),
  EmailSignUpForm: dynamic(() => import('@/components/BuilderIO/forms/EmailSignUpForm/EmailSignUpForm')),
  CopyBlock: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/CopyBlock')),
  DiamondColorScaleGraphic: dynamic(
    () => import('@/components/BuilderIO/buyingGuideComponents/DiamondColorScaleGraphic')
  ),
  DiamondCharacteristicsDiagram: dynamic(
    () => import('@/components/BuilderIO/buyingGuideComponents/DiamondCharacteristicsDiagram')
  ),
  StoreInfoBlock: dynamic(() => import('@/components/BuilderIO/common/StoreInfoBlock/StoreInfoBlock')),
  StoreLocator: dynamic(() => import('@/components/BuilderIO/common/StoreLocator/StoreLocator')),
  Faqs: dynamic(() => import('@/components/BuilderIO/common/Faqs/Faqs')),
  GatewayPromoTile: dynamic(() => import('@/components/BuilderIO/common/GatewayPromoTile/GatewayPromoTile')),
  ProductCarousel: dynamic(() => import('@/components/BuilderIO/common/ProductCarousel/ProductCarousel')),
  AsideTag: withChildren(dynamic(() => import('@/components/BuilderIO/common/AsideTag/AsideTag'))),
  MainTag: withChildren(dynamic(() => import('@/components/BuilderIO/common/MainTag/MainTag'))),
  ArticleTag: withChildren(dynamic(() => import('@/components/BuilderIO/common/ArticleTag/ArticleTag'))),
  DoubleImageBanner: dynamic(() => import('@/components/BuilderIO/common/DoubleImageBanner/DoubleImageBanner')),
  TwoLineSlimLineBanner: dynamic(
    () => import('@/components/BuilderIO/common/TwoLineSlimLineBanner/TwoLineSlimLineBanner')
  ),
  DropdownSlimlineBanner: dynamic(
    () => import('@/components/BuilderIO/common/DropdownSlimlineBanner/DropdownSlimlineBanner')
  ),
  DoubleImageTile: dynamic(() => import('@/components/BuilderIO/common/DoubleImageTile/DoubleImageTile')),
  SingleImageTile: dynamic(() => import('@/components/BuilderIO/common/SingleImageTile/SingleImageTile')),
  StorytellingTiles: dynamic(() => import('@/components/BuilderIO/common/StorytellingTiles/StorytellingTiles')),
  ImageLinkTile: dynamic(() => import('@/components/BuilderIO/common/ImageLinkTile/ImageLinkTile')),
  MissionTile: dynamic(() => import('@/components/BuilderIO/common/MissionTile/MissionTile')),
  ProductCarouselVideo: dynamic(
    () => import('@/components/BuilderIO/common/ProductCarouselVideo/ProductCarouselVideo')
  ),
  SearchSpringCarousel: dynamic(
    () => import('@/components/BuilderIO/common/SearchSpringCarousel/SearchSpringCarousel'),
    { ssr: false }
  ),
  DashHudsonCarousel: dynamic(() => import('@/components/BuilderIO/common/DashHudsonCarousel/DashHudsonCarousel')),
  GatewayCarousel: dynamic(() => import('@/components/BuilderIO/common/GatewayCarousel/GatewayCarousel')),
  BoxWidget: dynamic(() => import('@/components/BuilderIO/common/BoxWidget/BoxWidget')),
  JazzHRTable: dynamic(() => import('@/components/BuilderIO/common/JazzHRTable/JazzHRTable')),
  TableOfContents: dynamic(() => import('@/components/BuilderIO/common/TableOfContents/TableOfContents')),
  LastUpdated: dynamic(() => import('@/components/BuilderIO/common/LastUpdated/LastUpdated')),
  XYChart: dynamic(() => import('@/components/BuilderIO/common/XYChart/XYChart')),
  ShopByShape: dynamic(() => import('@/components/BuilderIO/common/ShopByShape/ShopByShape')),
  ThreeImageTile: dynamic(() => import('@/components/BuilderIO/common/ThreeImageTile/ThreeImageTile')),
  NavarTrackInfo: dynamic(() => import('@/components/BuilderIO/common/NavarTrackInfo/NavarTrackInfo')),
  MultiImage: dynamic(() => import('@/components/BuilderIO/common/MultiImage/MultiImage')),
  Storytelling: dynamic(() => import('@/components/BuilderIO/common/Storytelling/Storytelling')),
  GeneralCarousel: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/GeneralCarousel')),
  LinkSection: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/LinkSection')),
  ThreeAcross: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/ThreeAcross')),
  BirthstoneCarousel: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/BirthstoneCarousel')),
  CaratSizingImage: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/CaratSizingImage')),
  CaratSliderHandGraphic: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/CaratSliderHandGraphic')),
  DiamondClarityChart: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/DiamondClarityChart')),
  DiamondColorScaleChart: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/DiamondColorScaleChart')),
  DiamondCutInteractive: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/DiamondCutInteractive')),
  DiamondCutChart: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/DiamondCutChart')),
  MoissaniteDiamondTable: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/MoissaniteDiamondTable')),
  SapphireChoosing: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/SapphireChoosing')),
  RingSizeTable: dynamic(() => import('@/components/BuilderIO/buyingGuideComponents/RingSizeTable')),
  PageHero: dynamic(() => import('@/components/BuilderIO/whyBuyFromBE/PageHero')),
  BeyondConflict: dynamic(() => import('@/components/BuilderIO/whyBuyFromBE/BeyondConflict')),
  RecycledMetals: dynamic(() => import('@/components/BuilderIO/whyBuyFromBE/RecycledMetals')),
  WhereWeSourceFrom: dynamic(() => import('@/components/BuilderIO/whyBuyFromBE/WhereWeSourceFrom')),
  WBTitles: dynamic(() => import('@/components/BuilderIO/whyBuyFromBE/WBTitles')),
  WBPackaging: dynamic(() => import('@/components/BuilderIO/whyBuyFromBE/WBPackaging')),
  CarbonFree: dynamic(() => import('@/components/BuilderIO/whyBuyFromBE/CarbonFree')),
  ColoredGemstones: dynamic(() => import('@/components/BuilderIO/whyBuyFromBE/ColoredGemstones')),
  GemstoneOrigin: dynamic(() => import('@/components/BuilderIO/whyBuyFromBE/GemstoneOrigin')),
  HeaderBanner: dynamic(() => import('@/components/BuilderIO/slimlinePromotionBanners/HeaderBanner')),
  SlimlinePromotionBanner: dynamic(
    () => import('@/components/BuilderIO/slimlinePromotionBanners/SlimlinePromotionBanner')
  ),
  RotatingPromotionBanner: dynamic(
    () => import('@/components/BuilderIO/slimlinePromotionBanners/RotatingPromotionBanner')
  ),
  FinancingRequestForm: dynamic(() => import('@/components/BuilderIO/forms/FinancingRequestForm/FinancingRequestForm')),
  ContactUsForm: dynamic(() => import('@/components/BuilderIO/forms/ContactUsForm/ContactUsForm')),
  TrackOrderForm: dynamic(() => import('@/components/BuilderIO/forms/TrackOrderForm/TrackOrderForm')),
  CustomerServiceHero: dynamic(() => import('@/components/BuilderIO/customerService/CustomerServiceHero')),
  CustomerServiceTiles: dynamic(() => import('@/components/BuilderIO/customerService/CustomerServiceTiles')),
  CustomerServiceNavigation: dynamic(() => import('@/components/BuilderIO/customerService/CustomerServiceNavigation')),
  CustomerServiceCopyBlockHalf: dynamic(
    () => import('@/components/BuilderIO/customerService/CustomerServiceCopyBlockHalf')
  ),
  CustomerServiceCopyBlockFull: dynamic(
    () => import('@/components/BuilderIO/customerService/CustomerServiceCopyBlockFull')
  ),
  FAQs: dynamic(() => import('@/components/BuilderIO/customerService/FAQs')),
  Table: dynamic(() => import('@/components/BuilderIO/customerService/Table')),
};

function registerComponents({ components, tabName }: { components: Record<string, Component>; tabName: string }) {
  if (Builder.isPreviewing) {
    const itemsAvailable = Object.keys(components)
      .map(key => (!components[key]?.hideFromInsertMenu ? { name: components[key].name } : null))
      .filter(Boolean);

    Builder.register('insertMenu', {
      name: tabName,
      items: itemsAvailable,
    });
  }

  const componentNames = Object.keys(components);
  componentNames.forEach(name => {
    if (name in reactComponents) {
      Builder.registerComponent(reactComponents[name], components[name]);
    }
  });
}

registerComponents(commonComponents);
registerComponents(buyingGuideComponents);
registerComponents(whyBuyFromBeComponents);
registerComponents(slimlinePromotionBanners);
registerComponents(formComponents);
registerComponents(customerServiceComponents);

const mapToData = item => item.map(({ data }) => data);

type BlockType =
  | { modelName: string; options?: any; userAttributes?: any; all?: boolean; cachebust?: boolean }
  | Function;

/*
 * Get blocks from Builder.io
 * @param {BlockType[]} blocks - Array of block names or objects with modelName and options
 * @param {string} apiKey - Builder.io API Key
 * @returns {Promise<any[]>} - Array of block data
 */

export function getBlocks(blocks: BlockType[]): any {
  return Promise.all(
    blocks.map(block => {
      if (typeof block === 'function') return block();

      if (block?.all) return builder.getAll(block.modelName, { apiKey, ...block?.options }).then(mapToData);

      return builder
        .get(block.modelName, {
          apiKey,
          ...block?.options,
          userAttributes: block?.userAttributes,
          cachebust: block?.cachebust,
        })
        .toPromise();
    })
  );
}

export const getBuilderMessages = builderData => {
  const isArrayOfObjects = Array.isArray(builderData) && builderData.every(item => typeof item === 'object' && !!item);
  const isObjectOfObjects = !!builderData && typeof builderData === 'object';

  let topPurpleBar;
  let rotatingPromos;
  if (isArrayOfObjects) {
    // Handle case where props is an array of objects
    const result = builderData?.find(item => item.topPurpleBar);
    topPurpleBar = result?.topPurpleBar;
    rotatingPromos = builderData
      .flatMap(item => item?.data?.blocks)
      .filter(block => block?.component?.name === 'Rotating Promotion Banner')[0]?.component;
  } else if (isObjectOfObjects) {
    // Handle case where props is an object with other objects as properties
    topPurpleBar = builderData?.promo?.topPurpleBar;
    rotatingPromos = builderData?.headerBanner?.blocks?.filter(
      block => block?.component?.name === 'Rotating Promotion Banner'
    )[0]?.component;
  }

  return { topPurpleBar, rotatingPromos };
};
